import React, { useEffect, useRef, useState } from 'react';
import { motion, useAnimation } from 'framer-motion';

/**
 * React 1:1 mirror of `SlideToAnswerView.java` (white theme refresh).
 *
 * Spec (dp = px here):
 *  - HANDLE_SIZE_DP = 110 → the Allure colour triangle logo is the
 *    draggable handle, rendered via `/allure_logo_transparent.png`
 *    (shadow baked into the PNG, no synthetic shadow).
 *  - ANSWER_STRIP: 20px wide × 50px tall, green #4CAF75 ⌃ chevrons
 *    centered above the handle, translateY = -(HANDLE/2 + 50/2 - 6).
 *  - DECLINE_STRIP: 50px wide × 20px tall, red #E24C57 ‹ chevrons at
 *    the bottom-left vertex of the handle,
 *    translateX = -(HANDLE × 0.32 + 50/2 - 4),
 *    translateY = HANDLE × 0.28 + 2.
 *  - THRESHOLD = 110px (both axes). Progress ≥ 0.9 completes.
 *  - Idle bounce: y 0 → -5px → 0, 1500ms repeat infinite.
 *  - Haptics: 16ms buzz at ≥55% travel, 50ms buzz on completion.
 *  - Keyboard fallback: Enter / ArrowUp = answer, Esc / ArrowLeft = decline.
 */
const HANDLE_SIZE = 110;
const HANDLE_BOX = HANDLE_SIZE + 20;
const ANSWER_STRIP_WIDTH = 24;
const ANSWER_STRIP_LENGTH = 46;
const DECLINE_STRIP_WIDTH = 24;
const DECLINE_STRIP_LENGTH = 46;
const THRESHOLD = 110;
const PREVIEW_BUZZ = 0.55;

const ANSWER_COLOR = '#1E9F57';
const DECLINE_COLOR = '#D63442';

const buzz = (ms) => {
  if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    try { navigator.vibrate(ms); } catch { /* unsupported */ }
  }
};

export const SlideToAnswer = ({ onAnswer, onDecline, dataTestId = 'slide-to-answer' }) => {
  const [axis, setAxis] = useState(0); // 0 = idle, 1 = vertical (answer), -1 = horizontal (decline)
  const [progress, setProgress] = useState({ answer: 0, decline: 0 });
  const [completed, setCompleted] = useState(false);
  const controls = useAnimation();
  const dragStateRef = useRef({ startX: 0, startY: 0, pointerId: null, previewed: false });

  // Idle bounce — y 0 → -5 → 0, 1500ms, infinite (Java startIdleBounce()).
  useEffect(() => {
    if (axis !== 0 || completed) return undefined;
    let cancelled = false;
    const loop = async () => {
      while (!cancelled) {
        await controls.start({ y: -5, transition: { duration: 0.4, ease: 'easeOut' } });
        if (cancelled) break;
        await controls.start({ y: 0, transition: { duration: 0.4, ease: 'easeIn' } });
        if (cancelled) break;
        await new Promise((resolve) => setTimeout(resolve, 700));
      }
    };
    loop();
    return () => { cancelled = true; };
  }, [axis, completed, controls]);

  const resetHandle = () => {
    setAxis(0);
    setProgress({ answer: 0, decline: 0 });
    controls.start({ x: 0, y: 0, transition: { duration: 0.24, ease: [0.17, 0.84, 0.44, 1] } });
  };

  const completeAnswer = async () => {
    setCompleted(true);
    buzz(50);
    await controls.start({ y: -THRESHOLD, scale: 0.68, opacity: 0, transition: { duration: 0.22 } });
    if (onAnswer) onAnswer();
  };

  const completeDecline = async () => {
    setCompleted(true);
    buzz(50);
    await controls.start({ x: -THRESHOLD, scale: 0.68, opacity: 0, transition: { duration: 0.22 } });
    if (onDecline) onDecline();
  };

  const handlePointerDown = (event) => {
    if (completed) return;
    const state = dragStateRef.current;
    state.startX = event.clientX;
    state.startY = event.clientY;
    state.pointerId = event.pointerId;
    state.previewed = false;
    setAxis(0);
    event.currentTarget.setPointerCapture(event.pointerId);
    controls.stop();
  };

  const handlePointerMove = (event) => {
    if (completed) return;
    const state = dragStateRef.current;
    if (state.pointerId !== event.pointerId) return;
    const dx = event.clientX - state.startX;
    const dy = event.clientY - state.startY;
    let currentAxis = axis;
    if (currentAxis === 0 && (Math.abs(dx) > 16 || Math.abs(dy) > 16)) {
      currentAxis = Math.abs(dy) > Math.abs(dx) ? 1 : -1;
      setAxis(currentAxis);
    }
    if (currentAxis === 1) {
      const translateY = Math.max(-THRESHOLD * 1.1, Math.min(0, dy));
      controls.set({ y: translateY, x: 0 });
      const nextProgress = Math.min(1, Math.abs(translateY) / THRESHOLD);
      setProgress({ answer: nextProgress, decline: 0 });
      if (!state.previewed && nextProgress > PREVIEW_BUZZ) { state.previewed = true; buzz(16); }
    } else if (currentAxis === -1) {
      const translateX = Math.max(-THRESHOLD * 1.1, Math.min(0, dx));
      controls.set({ x: translateX, y: 0 });
      const nextProgress = Math.min(1, Math.abs(translateX) / THRESHOLD);
      setProgress({ answer: 0, decline: nextProgress });
      if (!state.previewed && nextProgress > PREVIEW_BUZZ) { state.previewed = true; buzz(16); }
    }
  };

  const handlePointerEnd = (event) => {
    if (completed) return;
    const state = dragStateRef.current;
    if (state.pointerId !== event.pointerId) return;
    try { event.currentTarget.releasePointerCapture(event.pointerId); } catch { /* ignore */ }
    state.pointerId = null;
    if (axis === 1 && progress.answer >= 0.9) { completeAnswer(); return; }
    if (axis === -1 && progress.decline >= 0.9) { completeDecline(); return; }
    resetHandle();
  };

  const handleKeyDown = (event) => {
    if (completed) return;
    if (event.key === 'Enter' || event.key === 'ArrowUp') { event.preventDefault(); completeAnswer(); }
    else if (event.key === 'Escape' || event.key === 'ArrowLeft') { event.preventDefault(); completeDecline(); }
  };

  return (
    <div
      data-testid={dataTestId}
      style={{
        position: 'relative',
        width: '100%',
        height: 300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        touchAction: 'none',
        userSelect: 'none',
      }}
    >
      {/* Answer chevrons — green ⌃, above the top vertex of the triangle.
          V3 (React): pushed WELL above the handle so the strip's bottom
          clears the triangle's visible top. zIndex sits ABOVE the handle
          so these never get hidden behind the PNG; pointerEvents:none
          keeps the drag handle itself fully clickable. */}
      <ChevronStrip
        direction="up"
        color={ANSWER_COLOR}
        progress={progress.answer}
        width={ANSWER_STRIP_WIDTH}
        length={ANSWER_STRIP_LENGTH}
        style={{
          position: 'absolute',
          width: ANSWER_STRIP_WIDTH,
          height: ANSWER_STRIP_LENGTH,
          top: `calc(50% - ${HANDLE_SIZE / 2 + ANSWER_STRIP_LENGTH / 2 + 36}px)`,
          left: '50%',
          marginLeft: -ANSWER_STRIP_WIDTH / 2,
          zIndex: 2,
        }}
        dataTestId={`${dataTestId}-answer-chevrons`}
      />

      {/* Decline chevrons — red ‹, just outside the bottom-left vertex.
          V3 (React): pushed further left + down so the strip clearly
          sits outside the triangle's bottom-left corner. zIndex above
          the handle. */}
      <ChevronStrip
        direction="left"
        color={DECLINE_COLOR}
        progress={progress.decline}
        width={DECLINE_STRIP_LENGTH}
        length={DECLINE_STRIP_WIDTH}
        style={{
          position: 'absolute',
          width: DECLINE_STRIP_LENGTH,
          height: DECLINE_STRIP_WIDTH,
          top: `calc(50% + ${HANDLE_SIZE * 0.28 + 4}px)`,
          left: `calc(50% - ${HANDLE_SIZE * 0.32 + DECLINE_STRIP_LENGTH / 2 + 30}px)`,
          marginLeft: -DECLINE_STRIP_LENGTH / 2,
          marginTop: -DECLINE_STRIP_WIDTH / 2,
          zIndex: 2,
        }}
        dataTestId={`${dataTestId}-decline-chevrons`}
      />

      {/* Draggable Allure triangle handle — the PNG has its drop shadow baked in */}
      <motion.button
        type="button"
        data-testid={`${dataTestId}-handle`}
        aria-label="Slide up to answer, slide left to decline"
        onKeyDown={handleKeyDown}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
        animate={controls}
        initial={{ x: 0, y: 0, scale: 1, opacity: 1 }}
        style={{
          width: HANDLE_BOX,
          height: HANDLE_BOX,
          padding: 0,
          background: 'none',
          border: 'none',
          cursor: 'grab',
          touchAction: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img
          src="/allure_logo_transparent.png"
          alt=""
          aria-hidden="true"
          draggable={false}
          style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }}
        />
      </motion.button>
    </div>
  );
};

/** Mirror of `ArrowStripView` — three tiny chevrons marching with staggered alpha pulses. */
const ChevronStrip = ({ direction, color, progress, width, length, style, dataTestId }) => {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    let frame;
    const start = performance.now();
    const loop = (now) => {
      setPhase(((now - start) / 1300) % 1);
      frame = window.requestAnimationFrame(loop);
    };
    frame = window.requestAnimationFrame(loop);
    return () => window.cancelAnimationFrame(frame);
  }, []);

  // Alpha boosted so chevrons are clearly readable at rest.
  // Red (decline, direction=left) renders slightly larger than green
  // (answer, direction=up) because horizontal chevrons visually feel
  // thinner at the same pixel size — bump the red arrow to 9 so both
  // read with comparable weight on screen.
  const baseAlpha = 0.9 + 0.1 * progress;
  const arrow = direction === 'left' ? 9 : 6;
  const stroke = direction === 'left' ? 2.8 : 2.2;
  const gap = 5;
  const viewW = direction === 'up' ? width : length;
  const viewH = direction === 'up' ? length : width;

  // Static unroll of the 3 chevrons — the previous `.map()` was being
  // wrapped by the Emergent visual-editor instrumentation with a
  // `<span data-ve-dynamic>` INSIDE the <svg>, which the SVG parser
  // treated as a foreign element and silently skipped. Hoisting each
  // path to its own JSX literal bypasses the wrapper and the chevrons
  // actually paint on screen.
  const alphaFor = (i) => {
    let t = ((phase * 3) - i) % 3;
    if (t < 0) t += 3;
    const localPhase = Math.max(0, Math.min(1, 1 - t));
    return Math.min(1, baseAlpha * (0.3 + 0.7 * localPhase));
  };
  const chevronPath = (i) => {
    const offset = (arrow + gap) * i;
    if (direction === 'up') {
      const cy = viewH - offset - arrow / 2;
      const cx = viewW / 2;
      return `M ${cx - arrow / 1.3} ${cy + arrow / 2} L ${cx} ${cy - arrow / 2} L ${cx + arrow / 1.3} ${cy + arrow / 2}`;
    }
    const cx = viewW - offset - arrow / 2;
    const cy = viewH / 2;
    return `M ${cx + arrow / 2} ${cy - arrow / 1.3} L ${cx - arrow / 2} ${cy} L ${cx + arrow / 2} ${cy + arrow / 1.3}`;
  };

  return (
    <div data-testid={dataTestId} aria-hidden="true" style={{ ...style, pointerEvents: 'none' }}>
      <svg width="100%" height="100%" viewBox={`0 0 ${viewW} ${viewH}`} fill="none">
        {/* DO NOT refactor these 3 paths back into a `.map()` or
            `Array.from(...).map(...)`. The Emergent visual-editor
            instrumentation wraps dynamic JSX children with
            `<span data-ve-dynamic>` — which inside an <svg> is a
            foreign element, so the SVG parser silently skips the
            <path> descendants and the chevrons stop rendering (bbox
            returns {}). Keep them as static JSX literals. */}
        <path d={chevronPath(0)} stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" opacity={alphaFor(0)} />
        <path d={chevronPath(1)} stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" opacity={alphaFor(1)} />
        <path d={chevronPath(2)} stroke={color} strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" opacity={alphaFor(2)} />
      </svg>
    </div>
  );
};
